import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Department {
  id: string;
  nombre: string;
  descripcion: string | null;
  created_at: string;
  usuarios_count?: number;
}

export default function Departments() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [newDepartment, setNewDepartment] = useState({
    nombre: '',
    descripcion: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const { data: departments, error } = await supabase
        .from('departamentos')
        .select(`
          *,
          usuarios_count:usuarios(count)
        `)
        .order('nombre');

      if (error) throw error;

      // Transform data to handle count
      const transformedDepartments = departments?.map(dept => ({
        ...dept,
        usuarios_count: dept.usuarios_count?.[0]?.count || 0
      }));

      setDepartments(transformedDepartments || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setError('Error al cargar los departamentos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingDepartment) {
        const { error } = await supabase
          .from('departamentos')
          .update({
            nombre: newDepartment.nombre,
            descripcion: newDepartment.descripcion || null
          })
          .eq('id', editingDepartment.id);

        if (error) throw error;
        setSuccess('Departamento actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('departamentos')
          .insert([{
            nombre: newDepartment.nombre,
            descripcion: newDepartment.descripcion || null
          }]);

        if (error) throw error;
        setSuccess('Departamento creado correctamente');
      }

      setShowModal(false);
      setEditingDepartment(null);
      setNewDepartment({ nombre: '', descripcion: '' });
      fetchDepartments();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (department: Department) => {
    setEditingDepartment(department);
    setNewDepartment({
      nombre: department.nombre,
      descripcion: department.descripcion || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este departamento? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('departamentos')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Departamento eliminado correctamente');
      fetchDepartments();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const filteredDepartments = departments.filter(dept =>
    dept.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (dept.descripcion?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Departamentos</h1>
        <button 
          onClick={() => {
            setEditingDepartment(null);
            setNewDepartment({ nombre: '', descripcion: '' });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Departamento
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar departamentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Nombre</th>
                  <th className="pb-3 font-semibold text-gray-600">Descripción</th>
                  <th className="pb-3 font-semibold text-gray-600">Usuarios</th>
                  <th className="pb-3 font-semibold text-gray-600">Creado</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredDepartments.map((department) => (
                  <tr key={department.id} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center">
                        <Building2 className="w-5 h-5 text-blue-500 mr-2" />
                        {department.nombre}
                      </div>
                    </td>
                    <td className="py-3">{department.descripcion || '-'}</td>
                    <td className="py-3">{department.usuarios_count}</td>
                    <td className="py-3">
                      {new Date(department.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(department)}
                          className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                          title="Editar departamento"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(department.id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar departamento"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredDepartments.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">
                      <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron departamentos</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir/editar departamento */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingDepartment ? 'Editar Departamento' : 'Nuevo Departamento'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={newDepartment.nombre}
                  onChange={(e) => setNewDepartment({...newDepartment, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción
                </label>
                <textarea
                  rows={3}
                  value={newDepartment.descripcion}
                  onChange={(e) => setNewDepartment({...newDepartment, descripcion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingDepartment(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingDepartment ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}